import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LicenseBadge } from '@/components/fintech/LicenseBadge';
import { toast } from 'sonner';
import { Building2, User } from 'lucide-react';

export default function RegisterPage() {
  const { t } = useLanguage();
  const nav = useNavigate();
  const [type, setType] = useState('individual');

  // Personal (contact) info — used for both individual and business (contact person)
  const [personal, setPersonal] = useState({
    email: '', password: '', first_name: '', last_name: '', middle_name: '',
    phone: '', birth_date: '', citizenship: '',
  });

  // Company info — only used when type = business
  const [company, setCompany] = useState({
    legal_name: '', registration_number: '', country: '', address: '',
    tax_number: '', business_type: '', website: '',
    bank_name: '', bank_account: '', swift_bic: '',
  });

  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Create user account
      const r = await api.post('/auth/register', { ...personal, account_type: type });
      // Persist pending company details for after email verification
      if (type === 'business') {
        localStorage.setItem('concorde_pending_company', JSON.stringify(company));
      } else {
        localStorage.removeItem('concorde_pending_company');
      }
      toast.success(type === 'business' ? 'Brokerage account created. Verify your email to submit your company.' : 'Account created. Please verify your email.');
      const q = new URLSearchParams({ email: personal.email });
      if (r.data.dev_code) q.set('dev_code', r.data.dev_code);
      if (type === 'business') q.set('next', 'business-portal');
      nav(`/verify-email?${q.toString()}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally { setBusy(false); }
  };

  const updP = (k, v) => setPersonal(f => ({ ...f, [k]: v }));
  const updC = (k, v) => setCompany(f => ({ ...f, [k]: v }));

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-16 px-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-6">
          <Link to="/" className="inline-block">
            <img src="/logo.png" alt="Concorde Financial Limited" className="h-11 w-auto mx-auto" />
          </Link>
        </div>
        <Card className="bg-white">
          <CardContent className="p-8">
            <h1 className="font-serif text-2xl text-[hsl(var(--brand-navy-2))]">{t('auth.register_title')}</h1>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
              {type === 'business'
                ? 'Open a corporate brokerage account. Provide contact person details and legal information of your company.'
                : 'Open a personal brokerage account with Concorde Financial.'}
            </p>

            <Tabs value={type} onValueChange={setType} className="mt-6" data-testid="register-type-tabs">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="individual" data-testid="register-type-individual">
                  <User className="h-4 w-4 mr-1" /> {t('auth.individual')}
                </TabsTrigger>
                <TabsTrigger value="business" data-testid="register-type-business">
                  <Building2 className="h-4 w-4 mr-1" /> {t('auth.business')}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <form onSubmit={submit} className="mt-6 space-y-6" data-testid={`register-form-${type}`}>
              {/* Contact person / individual block */}
              <div className="space-y-4">
                <div className="text-xs uppercase tracking-widest text-[hsl(var(--brand-gold))]">
                  {type === 'business' ? 'Contact person' : 'Personal information'}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>{t('auth.first_name')} *</Label><Input required data-testid="register-first-name" value={personal.first_name} onChange={e => updP('first_name', e.target.value)} /></div>
                  <div><Label>{t('auth.last_name')} *</Label><Input required data-testid="register-last-name" value={personal.last_name} onChange={e => updP('last_name', e.target.value)} /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>{t('auth.email')} *</Label><Input required type="email" data-testid="register-email" value={personal.email} onChange={e => updP('email', e.target.value)} /></div>
                  <div><Label>{t('auth.password')} *</Label><Input required minLength={8} type="password" data-testid="register-password" value={personal.password} onChange={e => updP('password', e.target.value)} /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div><Label>{t('auth.phone')}</Label><Input data-testid="register-phone" value={personal.phone} onChange={e => updP('phone', e.target.value)} /></div>
                  {type === 'individual' && (<>
                    <div><Label>{t('auth.birth_date')}</Label><Input type="date" data-testid="register-birth-date" value={personal.birth_date} onChange={e => updP('birth_date', e.target.value)} /></div>
                    <div><Label>{t('auth.citizenship')}</Label><Input data-testid="register-citizenship" value={personal.citizenship} onChange={e => updP('citizenship', e.target.value)} /></div>
                  </>)}
                </div>
              </div>

              {/* Company block — only for business */}
              {type === 'business' && (
                <div className="space-y-4 pt-4 border-t" data-testid="register-company-section">
                  <div className="text-xs uppercase tracking-widest text-[hsl(var(--brand-gold))]">Company information</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div><Label>Legal name *</Label><Input required data-testid="register-legal-name" value={company.legal_name} onChange={e => updC('legal_name', e.target.value)} /></div>
                    <div><Label>Registration number *</Label><Input required data-testid="register-reg-number" value={company.registration_number} onChange={e => updC('registration_number', e.target.value)} /></div>
                    <div><Label>Country *</Label><Input required data-testid="register-country" value={company.country} onChange={e => updC('country', e.target.value)} /></div>
                    <div><Label>Tax / VAT number</Label><Input value={company.tax_number} onChange={e => updC('tax_number', e.target.value)} /></div>
                    <div className="sm:col-span-2"><Label>Registered address *</Label><Input required data-testid="register-address" value={company.address} onChange={e => updC('address', e.target.value)} /></div>
                    <div><Label>Business type</Label><Input placeholder="LLC, JSC, Ltd" value={company.business_type} onChange={e => updC('business_type', e.target.value)} /></div>
                    <div><Label>Website</Label><Input value={company.website} onChange={e => updC('website', e.target.value)} /></div>
                    <div><Label>Bank name</Label><Input value={company.bank_name} onChange={e => updC('bank_name', e.target.value)} /></div>
                    <div><Label>Bank account (IBAN)</Label><Input value={company.bank_account} onChange={e => updC('bank_account', e.target.value)} /></div>
                    <div><Label>SWIFT / BIC</Label><Input value={company.swift_bic} onChange={e => updC('swift_bic', e.target.value)} /></div>
                  </div>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">Your company will be submitted for compliance review immediately after email verification. Corporate brokerage services activate once verification is complete.</p>
                </div>
              )}

              <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('auth.terms')}</p>
              <Button type="submit" disabled={busy} data-testid="register-submit-button" className="w-full bg-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy-2))]">
                {type === 'business' ? 'Create brokerage account & submit company' : t('auth.submit_register')}
              </Button>
            </form>

            <div className="mt-4 text-center text-sm text-[hsl(var(--muted-foreground))]">
              {t('auth.have_account')} <Link to="/login" data-testid="register-login-link" className="text-[hsl(var(--brand-navy))] font-medium">{t('header.login')}</Link>
            </div>
          </CardContent>
        </Card>
        <div className="mt-6 flex justify-center"><LicenseBadge /></div>
      </div>
    </div>
  );
}
